import {inject, Injectable, isDevMode} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {BehaviorSubject, distinctUntilChanged, map, Observable, of, shareReplay, Subject, switchMap, tap} from 'rxjs';
import {GeneralNotificationsRoot, NotificationsCategory} from '../../models/notifications.model';
import {UserService} from '../user/user.service';
import {toObservable} from '@angular/core/rxjs-interop';
import {endpoints} from '../endpoints';
import {BaseResponseApiRoot} from '../../models/user.model';
import {IS_SILENT_ERROR} from '../../interceptors/error/error-context';

@Injectable({
  providedIn: 'root'
})
export class GeneralNotificationsService {
  private http = inject(HttpClient);
  private userService = inject(UserService);

  /**
   * Subject interno per mantenere lo stato corrente delle notifiche e permetterne l'aggiornamento reattivo.
   */
  private notificationsSubject = new BehaviorSubject<GeneralNotificationsRoot>([]);

  /**
   * Subject per notificare l'avvenuta lettura di una notifica ad altri servizi.
   */
  private notificationReadSource = new Subject<{ id: number; categoria: NotificationsCategory }>();

  /**
   * Observable pubblico che espone gli eventi di lettura delle notifiche.
   */
  readonly notificationRead$ = this.notificationReadSource.asObservable();

  /**
   * Observable pubblico che espone la lista delle notifiche.
   * Si aggiorna automaticamente ogni volta che l'utente corrente cambia.
   */
  readonly notifications$: Observable<GeneralNotificationsRoot> = toObservable(this.userService.currentUser).pipe(
    distinctUntilChanged((a, b) => a?.idUtente === b?.idUtente),
    switchMap(user => {
      // Se non c'è un utente loggato, resettiamo le notifiche
      if (!user) {
        this.notificationsSubject.next([]);
        return of([]);
      }
      // Carichiamo le notifiche per il nuovo utente
      return this.getNotifications().pipe(
        map(notifications => this.applyBusinessLogic(notifications)),
        tap(notifications => this.notificationsSubject.next(notifications))
      );
    }),
    switchMap(() => this.notificationsSubject.asObservable()),
    shareReplay(1)
  );

  /**
   * Applica logiche di business specifiche (es. visualizzazione condizionale basata su sessione).
   */
  private applyBusinessLogic(notifications: GeneralNotificationsRoot): GeneralNotificationsRoot {
    return notifications.filter(note => {
      if (note.categoria === 'canone' && note.fase === 1) {
        const shownKey = 'payment_notice_shown';
        if (sessionStorage.getItem(shownKey)) {
          return false;
        }
        sessionStorage.setItem(shownKey, 'true');
      }
      return true;
    });
  }

  /**
   * Recupera la lista delle notifiche generali.
   */
  getNotifications(): Observable<GeneralNotificationsRoot> {
    return this.http.get<BaseResponseApiRoot<GeneralNotificationsRoot>>(endpoints.generalNotificationsEndpoint, {
      context: new HttpContext().set(IS_SILENT_ERROR, true)
    })
    .pipe(
      map(res =>
        (res?.res || []).map(item => ({
          ...item,
          read: false
        }))
      )
    );
  }

  /**
   * Forza una nuova emissione delle notifiche correnti senza refetch dal backend.
   * Utile per ri-triggerare i subscriber dopo che cambia una condizione esterna (es. primoAccesso).
   */
  refreshNotifications(): void {
    this.notificationsSubject.next(this.notificationsSubject.value);
  }

  /**
   * Rimuove una specifica notifica (es. versione già letta) dalla lista locale.
   * @param id Identificativo della notifica da rimuovere.
   */
  removeNotification(id: number): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.filter(n => n.id !== id);
    this.notificationsSubject.next(updatedNotifications);
  }

  /**
   * Segna una specifica notifica come letta localmente e sul server.
   * @param id Identificativo della notifica da aggiornare.
   * @param categoria Categoria della notifica (opzionale, se non fornita viene cercata localmente).
   */
  markAsRead(id: number, categoria?: NotificationsCategory): void {
    const currentNotifications = this.notificationsSubject.value;
    const notificationToUpdate = currentNotifications.find(n => n.id === id);

    if (notificationToUpdate?.read) {
      return;
    }

    const cat = categoria || notificationToUpdate?.categoria;

    if (!cat) {
      if (!categoria) {
        if (isDevMode()) console.warn('Notifica non trovata e categoria non fornita');
        return;
      }
      return;
    }

    // Solo per queste categorie il backend memorizza lo stato di lettura: per le
    // altre (canone, tickets) la lettura è effimera e vive nello stato locale.
    const letturaPersistita = cat === 'avvisi' || cat === 'versioni' || cat === 'promemoria';

    if (letturaPersistita) {
      const paramName = cat === 'avvisi' ? 'idAvviso' : cat === 'versioni' ? 'idVersione' : 'idPromemoria';
      const url = `${endpoints.readNotificationEndpoint}?${paramName}=${id}`;

      this.http.post<BaseResponseApiRoot<number>>(url, {}).subscribe({
        next: (res) => {
          // Lo stato locale si aggiorna solo a conferma avvenuta: se il server
          // non registra la lettura, la notifica resta non letta anche a schermo.
          if (res.res === 1) {
            this.updateLocalStateAfterRead(id, cat);
          }
          else {
            if (isDevMode()) console.error('Errore durante l\'aggiornamento della notifica sul server');
          }
        },
        error: () => { if (isDevMode()) console.error('Errore durante l\'aggiornamento della notifica sul server'); }
      });
    }
    else {
      // Nessuna persistenza lato server: si aggiorna direttamente lo stato locale.
      this.updateLocalStateAfterRead(id, cat);
    }
  }

  /**
   * Aggiorna lo stato locale dopo una lettura confermata.
   */
  private updateLocalStateAfterRead(id: number, categoria: NotificationsCategory): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(n =>
      n.id === id ? {...n, read: true} : n
    );
    this.notificationsSubject.next(updatedNotifications);

    // Notifichiamo ad altri servizi l'avvenuta lettura
    this.notificationReadSource.next({id, categoria});
  }
}
